import { jsPDF } from 'jspdf';

function safeFilename(s) {
  return (s || 'notes').replace(/[^a-z0-9-_]+/gi, '_').slice(0, 60);
}

function todayStamp() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function formatNotesAsMarkdown(notes, paper) {
  const title = paper?.name || 'Paper';
  const lines = [`# Notes — ${title}`, '', `*Exported ${todayStamp()}*`, ''];
  if (notes.length === 0) {
    lines.push('_No notes yet._');
    return lines.join('\n');
  }
  for (const note of notes) {
    lines.push(`## Page ${note.page_number}`);
    if (note.paragraph_excerpt) lines.push('', `> ${note.paragraph_excerpt}`);
    lines.push('', note.body, '');
  }
  return lines.join('\n');
}

export function formatNotesAsText(notes, paper) {
  const title = paper?.name || 'Paper';
  const lines = [`Notes — ${title}`, `Exported ${todayStamp()}`, ''];
  if (notes.length === 0) {
    lines.push('No notes yet.');
    return lines.join('\n');
  }
  for (const note of notes) {
    lines.push(`— Page ${note.page_number} —`);
    if (note.paragraph_excerpt) lines.push(`  "${note.paragraph_excerpt}"`);
    lines.push(note.body, '');
  }
  return lines.join('\n');
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export function exportNotesAsMarkdown(notes, paper) {
  const md = formatNotesAsMarkdown(notes, paper);
  const blob = new Blob([md], { type: 'text/markdown;charset=utf-8' });
  downloadBlob(blob, `${safeFilename(paper?.name)}_notes_${todayStamp()}.md`);
}

export function exportNotesAsPDF(notes, paper) {
  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
  const margin = 48;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const usableWidth = pageWidth - margin * 2;
  let y = margin;

  const ensureSpace = (needed) => {
    if (y + needed > pageHeight - margin) {
      doc.addPage();
      y = margin;
    }
  };

  const writeWrapped = (text, fontSize, opts = {}) => {
    doc.setFontSize(fontSize);
    if (opts.bold) doc.setFont(undefined, 'bold');
    else doc.setFont(undefined, 'normal');
    if (opts.italic) doc.setFont(undefined, 'italic');
    if (opts.color) doc.setTextColor(...opts.color); else doc.setTextColor(20, 20, 20);
    const lines = doc.splitTextToSize(text, usableWidth - (opts.indent || 0));
    const lineHeight = fontSize * 1.35;
    for (const line of lines) {
      ensureSpace(lineHeight);
      doc.text(line, margin + (opts.indent || 0), y);
      y += lineHeight;
    }
  };

  // Title block
  writeWrapped(`Notes — ${paper?.name || 'Paper'}`, 18, { bold: true });
  y += 6;
  writeWrapped(`Exported ${todayStamp()}`, 10, { italic: true, color: [120, 120, 120] });
  y += 14;

  if (notes.length === 0) {
    writeWrapped('No notes yet.', 12);
  } else {
    for (const note of notes) {
      ensureSpace(40);
      writeWrapped(`Page ${note.page_number}`, 13, { bold: true, color: [180, 90, 0] });
      if (note.paragraph_excerpt) {
        writeWrapped(`"${note.paragraph_excerpt}"`, 10, { italic: true, color: [110, 110, 110], indent: 12 });
      }
      writeWrapped(note.body, 11);
      y += 10;
    }
  }

  doc.save(`${safeFilename(paper?.name)}_notes_${todayStamp()}.pdf`);
}

export async function copyNotesToClipboard(notes, paper) {
  const text = formatNotesAsText(notes, paper);
  await navigator.clipboard.writeText(text);
}
